import type { ResizeOptions, WatermarkOptions, RenameOptions } from '../types';

const DB_NAME = 'rehub-tools';
const DB_VERSION = 1;
const FILES_STORE = 'files';
const SETTINGS_STORE = 'settings';

interface StoredFile {
  id: string;
  name: string;
  type: string;
  width: number;
  height: number;
  data: ArrayBuffer;
}

interface StoredSettings {
  key: 'job';
  resize: ResizeOptions;
  watermark: Omit<WatermarkOptions, 'logoFile' | 'logoPreviewUrl'>;
  watermarkLogoName: string | null;
  watermarkLogoType: string | null;
  watermarkLogoData: ArrayBuffer | null;
  rename: RenameOptions;
  savedAt: number;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(FILES_STORE)) {
        db.createObjectStore(FILES_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(SETTINGS_STORE)) {
        db.createObjectStore(SETTINGS_STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function fileToArrayBuffer(file: File): Promise<ArrayBuffer> {
  return file.arrayBuffer();
}

export async function saveJob(
  files: { id: string; file: File; originalWidth: number; originalHeight: number }[],
  resize: ResizeOptions,
  watermark: WatermarkOptions,
  rename: RenameOptions
): Promise<void> {
  const db = await openDB();

  const clearTx = db.transaction([FILES_STORE, SETTINGS_STORE], 'readwrite');
  clearTx.objectStore(FILES_STORE).clear();
  clearTx.objectStore(SETTINGS_STORE).clear();
  await new Promise<void>((resolve, reject) => {
    clearTx.oncomplete = () => resolve();
    clearTx.onerror = () => reject(clearTx.error);
  });

  const fileTx = db.transaction(FILES_STORE, 'readwrite');
  const fileStore = fileTx.objectStore(FILES_STORE);
  for (const f of files) {
    const data = await fileToArrayBuffer(f.file);
    const stored: StoredFile = {
      id: f.id,
      name: f.file.name,
      type: f.file.type,
      width: f.originalWidth,
      height: f.originalHeight,
      data,
    };
    fileStore.put(stored);
  }
  await new Promise<void>((resolve, reject) => {
    fileTx.oncomplete = () => resolve();
    fileTx.onerror = () => reject(fileTx.error);
  });

  let logoData: ArrayBuffer | null = null;
  if (watermark.logoFile) {
    logoData = await fileToArrayBuffer(watermark.logoFile);
  }

  const settingsTx = db.transaction(SETTINGS_STORE, 'readwrite');
  const settings: StoredSettings = {
    key: 'job',
    resize,
    watermark: {
      enabled: watermark.enabled,
      position: watermark.position,
      scale: watermark.scale,
      opacity: watermark.opacity,
      margin: watermark.margin,
    },
    watermarkLogoName: watermark.logoFile?.name ?? null,
    watermarkLogoType: watermark.logoFile?.type ?? null,
    watermarkLogoData: logoData,
    rename,
    savedAt: Date.now(),
  };
  settingsTx.objectStore(SETTINGS_STORE).put(settings);
  await new Promise<void>((resolve, reject) => {
    settingsTx.oncomplete = () => resolve();
    settingsTx.onerror = () => reject(settingsTx.error);
  });

  db.close();
}

export interface RestoredJob {
  files: { id: string; file: File; originalWidth: number; originalHeight: number }[];
  resize: ResizeOptions;
  watermark: WatermarkOptions;
  rename: RenameOptions;
}

export async function restoreJob(): Promise<RestoredJob | null> {
  let db: IDBDatabase;
  try {
    db = await openDB();
  } catch {
    return null;
  }

  try {
    const settingsTx = db.transaction(SETTINGS_STORE, 'readonly');
    const settingsReq = settingsTx.objectStore(SETTINGS_STORE).get('job');
    const settings = await new Promise<StoredSettings | undefined>((resolve, reject) => {
      settingsReq.onsuccess = () => resolve(settingsReq.result);
      settingsReq.onerror = () => reject(settingsReq.error);
    });

    if (!settings) {
      db.close();
      return null;
    }

    // Expire after 1 hour
    if (Date.now() - settings.savedAt > 60 * 60 * 1000) {
      await clearJob();
      db.close();
      return null;
    }

    const filesTx = db.transaction(FILES_STORE, 'readonly');
    const filesReq = filesTx.objectStore(FILES_STORE).getAll();
    const storedFiles = await new Promise<StoredFile[]>((resolve, reject) => {
      filesReq.onsuccess = () => resolve(filesReq.result || []);
      filesReq.onerror = () => reject(filesReq.error);
    });

    if (storedFiles.length === 0) {
      db.close();
      return null;
    }

    const files = storedFiles.map((sf) => ({
      id: sf.id,
      file: new File([sf.data], sf.name, { type: sf.type }),
      originalWidth: sf.width,
      originalHeight: sf.height,
    }));

    let logoFile: File | null = null;
    let logoPreviewUrl: string | null = null;
    if (settings.watermarkLogoData && settings.watermarkLogoName) {
      logoFile = new File([settings.watermarkLogoData], settings.watermarkLogoName, {
        type: settings.watermarkLogoType || 'image/png',
      });
      logoPreviewUrl = URL.createObjectURL(logoFile);
    }

    const watermark: WatermarkOptions = {
      ...settings.watermark,
      logoFile,
      logoPreviewUrl,
    };

    db.close();
    // Backfill defaults so jobs saved before the 360 mode update still restore cleanly.
    const resize: ResizeOptions = {
      mode: (settings.resize as Partial<ResizeOptions>).mode ?? 'standard',
      targetWidth: settings.resize.targetWidth ?? null,
      quality: (settings.resize as Partial<ResizeOptions>).quality ?? null,
    };
    return { files, resize, watermark, rename: settings.rename };
  } catch {
    db.close();
    return null;
  }
}

export async function clearJob(): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction([FILES_STORE, SETTINGS_STORE], 'readwrite');
    tx.objectStore(FILES_STORE).clear();
    tx.objectStore(SETTINGS_STORE).clear();
    await new Promise<void>((resolve) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
    db.close();
  } catch {
    // Non-critical
  }
}

export async function hasSavedJob(): Promise<boolean> {
  try {
    const db = await openDB();
    const tx = db.transaction(SETTINGS_STORE, 'readonly');
    const req = tx.objectStore(SETTINGS_STORE).get('job');
    const result = await new Promise<StoredSettings | undefined>((resolve) => {
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(undefined);
    });
    db.close();
    if (!result) return false;
    return Date.now() - result.savedAt < 60 * 60 * 1000;
  } catch {
    return false;
  }
}
